import { z } from 'zod';
import type { ShopProduktUebertragung } from './shopUebertragung';

/**
 * Status wie MP_STATUS der PHP-Middleware: neu (In Prüfung), geprueft (Freigabe ausstehend), abgelehnt,
 * gelistet, entfernt. Neu ist „freigegeben“: vom Marktplatz geprüft, die Listung im Marktplatz-Shop
 * übernimmt später die Shop-Anbindung (bisher legte die Freigabe das Produkt sofort in Shopify an).
 */
export const PRODUKT_STATUS = ['neu', 'geprueft', 'freigegeben', 'gelistet', 'abgelehnt', 'entfernt'] as const;
export const ProduktStatus = z.enum(PRODUKT_STATUS);
export type ProduktStatus = z.infer<typeof ProduktStatus>;

export const PRODUKT_STATUS_TITEL: Record<ProduktStatus, string> = {
  neu: 'In Prüfung',
  geprueft: 'Freigabe ausstehend',
  freigegeben: 'Freigegeben',
  gelistet: 'Gelistet',
  abgelehnt: 'Abgelehnt',
  entfernt: 'Entfernt',
};

export interface ProduktZeile {
  id: string;
  titel: string;
  marke: string;
  /** erstes Bild (URL) */
  bild: string | null;
  bilder: number;
  haendlerId: string;
  haendlerFirma: string;
  kategorie: string;
  status: ProduktStatus;
  qualitaetOk: boolean;
  maengel: string[];
  varianten: number;
  /** SKU und EAN der ersten Variante */
  sku: string;
  ean: string;
  preisVon: number | null;
  preisBis: number | null;
  /** Summe über alle Varianten; null = kein Bestand bekannt */
  bestand: number | null;
  /** was fehlt (Kategorie, Marke, Pflichtfelder …) – für „Fehlt: … ✨ ergänzen“ */
  luecken: string[];
  erstelltAm: string;
}

export interface ProduktZaehler {
  /** ohne entfernte Produkte (wie „Alle“ in der PHP-Middleware) */
  alle: number;
  neu: number;
  geprueft: number;
  freigegeben: number;
  gelistet: number;
  abgelehnt: number;
  entfernt: number;
  /** nicht entfernte Produkte mit Mängeln */
  maengel: number;
}

export interface ProduktSeite {
  eintraege: ProduktZeile[];
  weiter: string | null;
  /** Treffer für Suche und Filter */
  gesamt: number;
  /** false: bei Suche und Kategorie wird nur bis 1 000 gezählt („mehr als …“) */
  gesamtGenau: boolean;
  /** je Status – über alle Produkte des Marktplatzes bzw. des gewählten Händlers */
  zaehler: ProduktZaehler;
}

export const PRODUKT_SORTIERUNG = { neu: 'Neueste zuerst', titel: 'Titel A–Z' } as const;
export type ProduktSortierung = keyof typeof PRODUKT_SORTIERUNG;

const jaNein = z.preprocess((v) => (v === 'true' || v === '1' || v === true ? true : v === 'false' || v === '0' || v === false ? false : v), z.boolean());

export const ProduktAbfrage = z.object({
  /** SKU oder EAN (Anfang) – ohne Treffer dort: Titel (Teilwort) */
  q: z.string().trim().max(100).optional(),
  status: ProduktStatus.optional(),
  haendler: z.string().uuid().optional(),
  kategorie: z.string().trim().max(120).optional(),
  /** nur Produkte mit Mängeln */
  maengel: jaNein.optional(),
  sortierung: z.enum(['neu', 'titel']).default('neu'),
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ProduktAbfrage = z.input<typeof ProduktAbfrage>;

export interface ProduktVariante {
  id: string;
  titel: string;
  optionen: Record<string, string>;
  sku: string;
  ean: string;
  preis: number | null;
  preisAlt: number | null;
  bestand: number | null;
}

export interface ProduktDetail {
  id: string;
  titel: string;
  beschreibung: string;
  marke: string;
  kategorie: string;
  quellKategorie: string;
  bilder: string[];
  /** Werte der Produktfelder (namespace.key → Wert) */
  felder: Record<string, string>;
  quelle: string;
  extId: string | null;
  haendler: { id: string; firma: string; status: string };
  status: ProduktStatus;
  statusGrund: string;
  statusAm: string | null;
  qualitaetOk: boolean;
  maengel: string[];
  hinweise: string[];
  geprueftAm: string | null;
  shopProduktId: string | null;
  handle: string | null;
  gelistetAm: string | null;
  /** Übertragung an den Marktplatz-Shop */
  shop: ShopProduktUebertragung;
  varianten: ProduktVariante[];
  /** vom Marktplatz geänderte Felder (eigene Fassung, bleibt bei Abgleichen erhalten): titel, preis, felder.dxl.modell … */
  mpFelder: string[];
  /** Produktfelder mit Wert, nach Gruppen der Branchenvorlage (Zustand & Lieferumfang, Produktdetails …) */
  merkmale: { gruppe: string; felder: { voll: string; name: string; wert: string; pflicht: boolean }[] }[];
  erstelltAm: string;
  geaendertAm: string;
}

/* ------------------------------------------------------------ Prüfung und Status */

/**
 * Ziele, die das Backoffice setzen darf – „gelistet“ setzt die Übertragung an den Marktplatz-Shop.
 * „entfernt“ nimmt ein gelistetes Produkt wieder aus dem Shop (mp_unpublish), erneut freigeben listet es wieder.
 */
export const PRODUKT_ZIELE = ['freigegeben', 'abgelehnt', 'geprueft', 'entfernt'] as const;
export type ProduktZiel = (typeof PRODUKT_ZIELE)[number];

/** Von welchen Status aus ein Ziel erreichbar ist */
export const PRODUKT_UEBERGAENGE: Record<ProduktZiel, ProduktStatus[]> = {
  freigegeben: ['neu', 'geprueft', 'abgelehnt', 'entfernt'],
  abgelehnt: ['neu', 'geprueft', 'freigegeben'],
  geprueft: ['freigegeben', 'abgelehnt'],
  entfernt: ['gelistet'],
};

/** Aktionen, die die Oberfläche je Status anbietet */
export const PRODUKT_AKTIONEN: Record<ProduktStatus, { ziel: ProduktZiel; titel: string }[]> = {
  neu: [{ ziel: 'freigegeben', titel: 'Freigeben' }, { ziel: 'abgelehnt', titel: 'Ablehnen' }],
  geprueft: [{ ziel: 'freigegeben', titel: 'Freigeben' }, { ziel: 'abgelehnt', titel: 'Ablehnen' }],
  freigegeben: [{ ziel: 'abgelehnt', titel: 'Ablehnen' }, { ziel: 'geprueft', titel: 'Zurücksetzen' }],
  abgelehnt: [{ ziel: 'freigegeben', titel: 'Doch freigeben' }, { ziel: 'geprueft', titel: 'Zurücksetzen' }],
  gelistet: [{ ziel: 'entfernt', titel: 'Aus dem Shop entfernen' }],
  entfernt: [{ ziel: 'freigegeben', titel: 'Erneut freigeben' }],
};

/** Statuswechsel für ein oder mehrere Produkte. Ablehnen braucht einen Grund (für den Händler). */
export const ProduktStatusSchema = z
  .object({
    ids: z.array(z.string().uuid()).min(1).max(500),
    status: z.enum(PRODUKT_ZIELE),
    grund: z.string().trim().max(500).default(''),
  })
  .superRefine((d, ctx) => {
    if (d.status === 'abgelehnt' && !d.grund) ctx.addIssue({ code: 'custom', path: ['grund'], message: 'Bitte einen Grund angeben – der Händler sieht ihn.' });
  });
export type ProduktStatusEingabe = z.input<typeof ProduktStatusSchema>;

export interface ProduktStatusErgebnis {
  geaendert: number;
  /** Produkte, die nicht wechseln konnten – mit Grund */
  abgewiesen: { id: string; titel: string; grund: string }[];
}

export const ProduktPruefenSchema = z.object({ ids: z.array(z.string().uuid()).min(1).max(500) });

export interface ProduktPruefErgebnis {
  geprueft: number;
  ok: number;
}

/* ------------------------------------------------------------ Eingang (Import, später Connector) */

const text = (max: number) => z.string().trim().max(max).default('');

export const ProduktVarianteEingabe = z.object({
  titel: text(200),
  optionen: z.record(z.string().max(60), z.string().max(120)).default({}),
  sku: text(120),
  ean: z.string().max(40).default('').transform((v) => v.replace(/\s+/g, '')),
  preis: z.number().min(0).max(9_999_999).nullable().default(null),
  preisAlt: z.number().min(0).max(9_999_999).nullable().default(null),
  bestand: z.number().int().min(0).nullable().default(null),
  extId: z.string().max(200).nullable().default(null),
});

export const ProduktEingabe = z.object({
  haendlerId: z.string().uuid(),
  titel: text(500),
  beschreibung: z.string().max(100_000).default(''),
  marke: text(160),
  kategorie: text(120),
  quellKategorie: text(300),
  /** nur http(s)-Adressen, doppelte entfernt */
  bilder: z
    .array(z.string().trim().max(2000))
    .max(50)
    .default([])
    .transform((l) => [...new Set(l.filter((u) => /^https?:\/\//i.test(u)))]),
  quelle: text(40).transform((v) => v || 'manuell'),
  extId: z.string().max(200).nullable().default(null),
  status: ProduktStatus.default('neu'),
  varianten: z.array(ProduktVarianteEingabe).max(250).default([]),
  /** Werte der Produktfelder nach Schlüssel (namespace.key) – übernommen werden nur freigegebene Felder */
  felder: z.record(z.string().max(130), z.union([z.string().max(2000), z.number(), z.boolean()])).default({}),
  /** Rohdaten aus dem Händler-Shop (Feldname → Wert); die Feldzuordnung füllt daraus die Produktfelder */
  rohdaten: z.record(z.string().max(200), z.unknown()).default({}),
});
export type ProduktEingabe = z.input<typeof ProduktEingabe>;

/** GTIN/EAN-Prüfziffer (Modulo 10) wie mp_gtin_checksum */
export function gtinPruefziffer(d: string): boolean {
  if (!/^\d{8,14}$/.test(d)) return false;
  let summe = 0;
  let faktor = 3;
  for (let i = d.length - 2; i >= 0; i--) {
    summe += Number(d[i]) * faktor;
    faktor = faktor === 3 ? 1 : 3;
  }
  return (10 - (summe % 10)) % 10 === Number(d[d.length - 1]);
}

/** Grober Wortfilter für offensichtlich unzulässige Artikel (mp_content_flags) */
const UNZULAESSIG: Record<string, string[]> = {
  'Waffen/Munition': ['schusswaffe', 'pistole', 'munition', 'patronen', 'schalldämpfer', 'sprengstoff', 'granate', 'firearm', 'ammunition'],
  Drogen: ['kokain', 'heroin', 'cannabis', 'marihuana', 'metamphetamin', 'crystal meth'],
  Sexuell: ['pornograf', 'sexspielzeug', 'dildo', 'vibrator', 'erotikartikel'],
  Gefälscht: ['replica rolex', 'fälschung', 'counterfeit', 'gefälschte marke', 'nachbau marken'],
  Extremismus: ['hakenkreuz', 'ss-runen', 'nsdap', 'volksverhetz'],
  Verschreibung: ['rezeptpflichtig', 'verschreibungspflichtig', 'anabolika', 'steroide'],
};

export function htmlZuText(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

export interface Pruefergebnis {
  ok: boolean;
  maengel: string[];
  hinweise: string[];
}

/**
 * Qualitätsregeln je Marktplatz (Marktplatz → Produktfelder → Qualität). Die Vorgaben entsprechen
 * mp_quality_check der PHP-Middleware; Pflichtfelder sind freigegebene Produktfelder, die ausgefüllt sein müssen.
 */
export interface Qualitaetsregeln {
  titelMin: number;
  /** 0 = Beschreibung wird nicht geprüft */
  beschreibungMin: number;
  bilderMin: number;
  eanPflicht: boolean;
  skuPflicht: boolean;
  kategoriePflicht: boolean;
  /** Marke muss angegeben sein (Vorgabe der Branchen Uhren und Schmuck) */
  markePflicht: boolean;
  /** Schlüssel (namespace.key) und Bezeichnung der Pflichtfelder */
  pflichtfelder?: { schluessel: string; name: string }[];
}

export const STANDARD_REGELN: Qualitaetsregeln = {
  titelMin: 5,
  /** 0 = Beschreibung ist freiwillig (Vorgabe seit Migration 0301) */
  beschreibungMin: 0,
  bilderMin: 1,
  eanPflicht: true,
  skuPflicht: true,
  kategoriePflicht: false,
  markePflicht: false,
  pflichtfelder: [],
};

/**
 * Qualitätsprüfung wie mp_quality_check: Titel, Bilder, Beschreibung und je Variante Preis > 0, SKU und gültige
 * EAN/GTIN – nach den Regeln des Marktplatzes (ohne Angabe: Vorgaben der PHP-Middleware). Dazu Kategorie und
 * Pflichtfelder, wenn der Marktplatz sie verlangt. Mängel sperren die Freigabe, Hinweise nicht.
 */
export function produktPruefen(
  p: {
    titel: string;
    beschreibung: string;
    bilder: string[];
    varianten: { sku: string; ean: string; preis: number | null }[];
    kategorie?: string;
    marke?: string;
    felder?: Record<string, string>;
  },
  regeln: Qualitaetsregeln = STANDARD_REGELN,
): Pruefergebnis {
  const maengel: string[] = [];
  const hinweise: string[] = [];

  const titel = p.titel.trim();
  if (!titel) maengel.push('Titel fehlt');
  else if ([...titel].length < regeln.titelMin) maengel.push('Titel zu kurz');
  else if ([...titel].length > 160) hinweise.push('Titel sehr lang (mehr als 160 Zeichen)');

  const bilder = p.bilder.filter((b) => b.trim()).length;
  if (bilder < regeln.bilderMin) maengel.push(regeln.bilderMin === 1 ? 'Mindestens 1 Bild erforderlich' : `Mindestens ${regeln.bilderMin} Bilder erforderlich (aktuell ${bilder})`);

  const inhalt = htmlZuText(p.beschreibung);
  if (regeln.beschreibungMin > 0) {
    if (!inhalt) maengel.push('SEO-Beschreibung fehlt');
    else if ([...inhalt].length < regeln.beschreibungMin) maengel.push(`SEO-Beschreibung zu kurz (min. ${regeln.beschreibungMin} Zeichen, aktuell ${[...inhalt].length})`);
  }

  const v = p.varianten;
  const bei = (n: number) => (v.length > 1 ? ` (${n} von ${v.length} Varianten)` : '');
  if (!v.length) {
    maengel.push(regeln.eanPflicht ? 'Keine Variante mit Preis, SKU und EAN' : 'Keine Variante mit Preis und SKU');
  } else {
    const ohnePreis = v.filter((x) => !x.preis || x.preis <= 0).length;
    if (ohnePreis) maengel.push(`Preis fehlt oder ist 0${bei(ohnePreis)}`);
    const eans = v.map((x) => x.ean.replace(/\D+/g, ''));
    const ohneEan = eans.filter((e) => !e).length;
    if (ohneEan && regeln.eanPflicht) maengel.push(`EAN/GTIN fehlt${bei(ohneEan)}`);
    const falsch = eans.filter((e) => e && ![8, 12, 13, 14].includes(e.length));
    if (falsch.length) maengel.push(v.length > 1 ? `EAN/GTIN ungültig${bei(falsch.length)}` : `EAN/GTIN ungültig (${falsch[0]!.length} Stellen)`);
    const ziffer = eans.filter((e) => [8, 12, 13, 14].includes(e.length) && !gtinPruefziffer(e)).length;
    if (ziffer) hinweise.push(`EAN-Prüfziffer stimmt nicht${bei(ziffer)}`);
    const ohneSku = v.filter((x) => !x.sku.trim()).length;
    if (ohneSku && regeln.skuPflicht) maengel.push(`SKU fehlt${bei(ohneSku)}`);
  }

  if (regeln.markePflicht && !(p.marke ?? '').trim()) maengel.push('Marke fehlt');
  if (regeln.kategoriePflicht && !(p.kategorie ?? '').trim()) maengel.push('Kategorie fehlt');
  for (const f of regeln.pflichtfelder ?? []) {
    if (!(p.felder?.[f.schluessel] ?? '').trim()) maengel.push(`Pflichtfeld fehlt: ${f.name}`);
  }

  const klein = `${titel} ${inhalt}`.toLowerCase();
  for (const [art, woerter] of Object.entries(UNZULAESSIG)) {
    if (woerter.some((w) => klein.includes(w))) maengel.push(`Unzulässiger Inhalt: ${art}`);
  }

  return { ok: maengel.length === 0, maengel, hinweise };
}
